import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { ClientService } from "@services";
import axios from "axios";
import { EditImageModal, Loader, Image } from "../../../../components";
import UploadTaxDocument from "../../components/UploadTaxDocument";

class DetailPanel extends Component {
	constructor(props) {
		super(props);

		this.state = {
			photoUrl: "",
			validImageSelected: true,
			loading: false,
			showEditImageModal: false,
			imageToEdit: "",
		};
	}

	onUploadClick = () => this._uploadInput.click();

	/**
	 * Uploads a tax exemption document for the client and retrieves the latest client details
	 * @param {Event} event The HTML event handled by this method
	 */
	uploadDocument = event => {
		const {
			getClient,
			client: { Id },
		} = this.props;
		const file = event?.target?.files[0] ?? null;

		if (!file) return;

		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			ClientService.uploadTaxExemptionDocument(Id, file)
				.then(({ data: { Message } }) => {
					utils.notify({
						message: Message,
						type: "success",
						title: "Upload Successful",
					});

					getClient(Id, true);
				})
				.catch(() => {
					utils.notify({
						message: "Error uploading document",
						type: "error",
					});
				});
		};
	};

	openEditImageModal = imageToEdit => this.setState({ showEditImageModal: true, imageToEdit });

	closeEditImageModal = () => this.setState({ showEditImageModal: false, imageToEdit: "" });

	/**
	 * Processes the selected image via `FileReader` and opens it in the Edit Image modal
	 * @param {Event} event The HTML event handled by this method
	 */
	uploadImage = event => {
		const input = event.target;

		if (!input.files || !input.files[0]) return;
		const [{ type }] = input.files;
		this.setState({ validImageSelected: type.includes("image") }, () => {
			if (type.includes("image")) {
				this.setState({ loading: true });

				const reader = new FileReader();
				reader.readAsDataURL(input.files[0]);

				reader.onload = e => {
					this.openEditImageModal(e.target.result);
					input.value = "";
				};
			}
		});
	};

	/**
	 * Uploads a new image as the client's logo
	 * @param {String|ArrayBuffer} imageToSend The image to upload after editing
	 */
	saveImage = imageToSend => {
		const { lang } = global;
		const {
			client: { Id: id, ClientLogo },
			getClient,
		} = this.props;
		ClientService.uploadClientImage({ id, file: imageToSend })
			.then(() => {
				if (!ClientLogo) {
					getClient();
					this.setState({ loading: false });
				} else {
					// Since this service call does not respond with the image we must make a call to the image URL from client
					// GET the Client Logo URL
					axios
						.get(`https://rollkalldev.blob.core.windows.net/client-logo/${id}`, {
							responseType: "arraybuffer",
						})
						.then(response => {
							// After uploading the image, get the most up to date image from the client and decode the response
							const decodedRes = Buffer.from(response.data, "binary").toString(
								"base64",
							);
							this.setState({
								photoUrl: `data:image/png;base64,${decodedRes}`,
								loading: false,
							});
						})
						.catch(() => {});
				}
			})
			.catch(() => {
				this.setState({ photoUrl: "", loading: false });
				utils.notify({
					message: lang.t("Error uploading client logo"),
					type: "error",
				});
			});
	};

	/**
	 * Renders a client statistic for the coordinator to view
	 * @param {Object} obj
	 * @param {String} obj.title The title of the statistic being shown
	 * @param {*} obj.value The value of the statistic being shown
	 */
	renderStats = ({ title, value = 0 }) => (
		<div className="col-xs-4 client-view-details-stat">
			<h6>{title}</h6>
			<h5>{value}</h5>
		</div>
	);

	render() {
		const { lang } = global;
		const { client } = this.props;
		const { photoUrl, validImageSelected, loading, imageToEdit, showEditImageModal } =
			this.state;

		return (
			<Fragment>
				<div id="client-view-details">
					<div id="client-view-details-image-container">
						<div
							className={`form-group image-uploader text-center' ${loading ? "loading" : ""}`}
						>
							<label htmlFor="upload-client-image">
								<Image
									src={photoUrl || client?.ClientLogo}
									fallbackSrc="default"
								/>
								<div className="blur">{lang.t("Update Image")}</div>
								<Loader
									active={loading}
									size="small"
								/>
							</label>
							<input
								id="upload-client-image"
								type="file"
								accept=".jpeg,.jpg,.png,.tif,.tiff"
								className="hidden"
								onChange={this.uploadImage}
							/>
						</div>
						<If condition={!validImageSelected}>
							<div className="text-center text-danger">
								{lang.t(
									"Please ensure the selected image has one of the following file extensions:",
								)}
								<br />
								{lang.t("jpeg, jpg, png, tif, tiff")}
							</div>
						</If>
						<div id="client-view-details-name">{client.Name}</div>
					</div>
					<div className="separator separator-dashed" />

					<div
						className="row"
						id="client-view-details-rkpay-container"
					>
						<div className="col-xs-6">
							<i className="rkf rkf-dollar-circle" />
							<small>{lang.t("Total Earnings")}</small>
							<span>{utils.currencyFormatter(client?.TotalEarnings ?? 0)}</span>
						</div>
						<div className="col-xs-6">
							<img
								src="/images/credit-card-payment.png"
								alt="card"
							/>
							<small>
								{`RKPay ${client.IsRKPay ? lang.t("Active") : lang.t("Inactive")}`}
							</small>
						</div>
					</div>
					<div className="separator separator-dashed" />

					<div className="row">
						{this.renderStats({
							title: lang.t("Total Jobs"),
							value: client.TotalJobCount || 0,
						})}
						{this.renderStats({
							title: lang.t("Total Locations"),
							value: client.TotalLocationCount || 0,
						})}
						{this.renderStats({
							title: lang.t("Total Hours Worked"),
							value: (client.TotalHoursWorked || 0).toFixed(2),
						})}
					</div>
					<div className="separator separator-dashed" />

					<div
						id="client-view-details-contact-container"
						hidden={!client.PrimaryContactId}
					>
						<small>{lang.t("Primary Contact")}</small>
						<div>
							<strong>{`${client.PrimaryContactFirstName} ${client.PrimaryContactLastName}`}</strong>
						</div>
						<a
							className="btn btn-link"
							hidden={!client.PrimaryContactEmail}
							href={`mailto:${client.PrimaryContactEmail}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							<i className="rkf rkf-email-question" />
							{lang.t("EMail")}
						</a>
						<a
							className="btn btn-link btn-small"
							hidden={!client.PrimaryContactPhoneNumber}
							href={`tel:${client.PrimaryContactPhoneNumber}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							<i className="rkf rkf rkf-phone" />
							{utils.formatPhoneNumber(client.PrimaryContactPhoneNumber)}
						</a>
					</div>
					<div
						className={`separator separator-dashed${client.PrimaryContactId ? "" : " hidden"}`}
					/>

					<h5>{` ${lang.t("Tax Exemption")} `}</h5>
					<UploadTaxDocument
						taxExemptionRequest={client.TaxExemptionRequest}
						uploadDocument={this.uploadDocument}
					/>
					<Loader active={!client.Name} />
				</div>
				<EditImageModal
					show={showEditImageModal}
					closeModal={this.closeEditImageModal}
					successCallback={this.saveImage}
					imageToEdit={imageToEdit}
				/>
			</Fragment>
		);
	}
}

DetailPanel.propTypes = {
	client: PropTypes.object,
	getClient: PropTypes.func.isRequired,
};

DetailPanel.defaultProps = {
	client: {},
};

export default DetailPanel;
